import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { InventoryItem } from '../../../accounting/inventory/types/inventory.types';
import {
  compareRecipeVersions,
  discardRecipeDraft,
  getRecipeDraft,
  publishRecipeDraft,
  RecipeVersionApiError,
  saveRecipeDraft,
} from '../../services/recipeVersionService';
import type { RecipeDiffRow, RecipeSnapshot, RecipeVersionDraftInfo } from '../../types/recipeVersion.types';
import RecipeDraftEditor from './RecipeDraftEditor';
import VersionCompareDialog from './VersionCompareDialog';

interface RecipeDraftTabProps {
  recipeId: string;
  readOnly: boolean;
  inventoryItems: InventoryItem[];
  draftInfo?: RecipeVersionDraftInfo | null;
  onChanged: () => void;
  onLiveChanged: () => void;
}

const RecipeDraftTab: React.FC<RecipeDraftTabProps> = ({
  recipeId,
  readOnly,
  inventoryItems,
  draftInfo,
  onChanged,
  onLiveChanged,
}) => {
  const { t } = useTranslation('production');
  const [snapshot, setSnapshot] = useState<RecipeSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [compareRows, setCompareRows] = useState<RecipeDiffRow[] | null>(null);
  const [staleDiff, setStaleDiff] = useState<RecipeDiffRow[] | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const detail = await getRecipeDraft(recipeId);
      setSnapshot(detail.snapshot);
    } catch (err) {
      setSnapshot(null);
      setError(err instanceof RecipeVersionApiError ? err.message : t('recipeVersions.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  const run = async (action: () => Promise<unknown>, live = false) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
      if (live) onLiveChanged();
    } catch (err) {
      if (err instanceof RecipeVersionApiError && err.code === 'DRAFT_STALE') {
        setStaleDiff(err.diff || []);
      } else {
        setError(err instanceof RecipeVersionApiError ? err.message : t('recipeVersions.actionFailed'));
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <CircularProgress size={24} />;
  if (!snapshot) return <Alert severity="error">{error || t('recipeVersions.loadFailed')}</Alert>;

  return (
    <Box>
      {draftInfo?.isStale && <Alert severity="warning" sx={{ mb: 1 }}>{t('recipeVersions.stale')}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <RecipeDraftEditor
        snapshot={snapshot}
        inventoryItems={inventoryItems}
        saving={busy}
        readOnly={readOnly}
        error={null}
        onCancel={() => { void load(); }}
        onSave={(next, draftNote) => {
          if (readOnly) return;
          void run(async () => {
            await saveRecipeDraft(recipeId, next, draftNote);
            setSnapshot(next);
          });
        }}
      />
      {!readOnly && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label={t('recipeVersions.note')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <Button variant="contained" disabled={busy} onClick={() => run(() => publishRecipeDraft(recipeId, false, note), true)}>
            {t('recipeVersions.publish')}
          </Button>
          <Button color="error" disabled={busy} onClick={() => run(() => discardRecipeDraft(recipeId))}>
            {t('recipeVersions.discard')}
          </Button>
          <Button disabled={busy} onClick={() => run(async () => setCompareRows(await compareRecipeVersions(recipeId, 'draft', 'live')))}>
            {t('recipeVersions.compareDraft')}
          </Button>
        </Box>
      )}
      <VersionCompareDialog open={compareRows != null} rows={compareRows || []} onClose={() => setCompareRows(null)} />
      <VersionCompareDialog
        open={staleDiff != null}
        title={t('recipeVersions.stale')}
        rows={staleDiff || []}
        onClose={() => setStaleDiff(null)}
        extra={!readOnly ? (
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              setStaleDiff(null);
              void run(() => publishRecipeDraft(recipeId, true, note), true);
            }}
          >
            {t('recipeVersions.forcePublish')}
          </Button>
        ) : undefined}
      />
    </Box>
  );
};

export default RecipeDraftTab;
